import React, { useState } from "react";

import { mdiBroom, mdiCalendarToday } from "@mdi/js";
import Icon from "@mdi/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import moment from "moment";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { toast } from "sonner";

import computeAdsObjectiveCompletion from "./adsObjectiveCompletion";
import CalendarDaySidepanelTimeslotList from "./CalendarDaySidepanelTimeslotList";
import CalendarDaySidepanelVariantList from "./CalendarDaySidepanelVariantList";
import FastAPI from "../../../api/FastAPI";
import { API_DATE_FORMAT } from "../../../constants/dateFormat";
import useFAST from "../../../contexts/FASTContext";
import useAbility from "../../../hooks/useAbility";
import SidepanelBlock from "../../block/SidepanelBlock";
import { BUTTON_VARIANTS } from "../../button/Button";
import Input from "../../form/Input";
import ConfirmModal from "../../modal/ConfirmModal";
import SidePanel from "../../sidePanel/SidePanel";
import ObjectiveComponent from "../ObjectiveComponent";

import type { JSX } from "react";

import type { DeleteScheduleResponse } from "../../../api/FastAPI.types";
import type { AppState } from "../../../reducers";
import type { FASTChannelRoutesParams } from "../../routes/FASTChannelRoutes";

interface CalendarDayDetailsSidePanelProps {
  isOpen: boolean;
  onClose: () => void;
  currentDay: Date;
  playlistDuration: string;
}

export default function CalendarDayDetailsSidePanel({
  isOpen,
  onClose,
  currentDay,
  playlistDuration,
}: CalendarDayDetailsSidePanelProps): JSX.Element | null {
  const orgaOptions = useSelector(
    (state: AppState) =>
      state.fetchReducer.orga_config.data?.organization_options,
  );
  const { canAccess } = useAbility();
  const { fastChannelsWrite: canAccessfastChannelsWrite } =
    canAccess(orgaOptions);
  const { channelId } = useParams<FASTChannelRoutesParams>();

  const { state } = useFAST();
  const { schedules } = state;
  const queryClient = useQueryClient();
  const [showModal, setShowModal] = useState(false);
  const { t } = useTranslation("editor-fast-channel");

  const methods = useForm({
    values: {
      playlistDuration,
    },
  });

  const title = (
    <div className="flex flex-wrap items-center gap-[0.9375rem]">
      <Icon path={mdiCalendarToday} size={0.9375} />
      <div>
        {currentDay.toLocaleDateString()} - {t("scheduleDetails")}
      </div>
    </div>
  );

  const formatedDay = moment(currentDay).format(API_DATE_FORMAT);
  const defaultSchedule = schedules.find((schedule) => schedule.default);
  const currentDayIsBeforeTomorrow = moment(currentDay).isBefore(
    moment().add(1, "day").startOf("day"),
  );

  const getScheduleKpisQuery = FastAPI.generateQuery({
    fn: FastAPI.getScheduleKpis,
    params: [
      {
        channelId,
        scheduleId: defaultSchedule?.uuid ?? "",
        day: formatedDay,
      },
    ],
  });

  const getScheduleDetailsQuery = FastAPI.generateQuery({
    fn: FastAPI.getScheduleDetails,
    params: [
      {
        channelId,
        day: formatedDay,
      },
    ],
  });

  const deleteScheduleMutation = useMutation({
    mutationFn: async ({
      scheduleId,
    }: {
      scheduleId: string;
    }): Promise<DeleteScheduleResponse> =>
      FastAPI.deleteSchedule({
        channelId,
        scheduleId,
        date: formatedDay,
      }),
    onSuccess: (): void => {
      queryClient.invalidateQueries({
        queryKey: ["getCalendarFilling"],
      });
      queryClient.invalidateQueries({
        queryKey: ["getScheduleAllScheduleList"],
      });
      queryClient.invalidateQueries({
        queryKey: ["getScheduleVariantDetails"],
      });
      queryClient.invalidateQueries({
        queryKey: ["getCalendarFillingWeek"],
      });
      toast.success(t("calendarPlaylistDeleteSuccess"));
    },
    onError: (error): void => {
      toast.error(t("calendarPlaylistDeleteError"));
      console.error(error);
    },
    onSettled: (): void => {
      setShowModal(false);
    },
  });

  const { data: dailyChannelKpis } = useQuery({
    ...getScheduleKpisQuery,
    enabled: Boolean(channelId && isOpen),
    keepPreviousData: true,
  });

  const { data: scheduleDetails } = useQuery({
    ...getScheduleDetailsQuery,
    enabled: Boolean(isOpen),
  });

  if (!defaultSchedule) {
    return null;
  }

  const variantList = scheduleDetails?.data.variants ?? [];
  const timeslotList = scheduleDetails?.data.timeslots ?? [];

  const ads = {
    duration: dailyChannelKpis?.data.ads_duration,
    completion: computeAdsObjectiveCompletion(dailyChannelKpis),
  };

  const program = {
    duration: dailyChannelKpis?.data.programs_duration,
    completion: [{ value: dailyChannelKpis?.data.programs_completion ?? 0 }],
  };

  const noProgramsOrAds = !ads.duration && !program.duration;

  const isDeleteDisabled = currentDayIsBeforeTomorrow || noProgramsOrAds;

  const tooltipContent = currentDayIsBeforeTomorrow
    ? t("calendarPlaylistTooltipDeletePastError")
    : t("calendarPlaylistTooltipDeleteNoProgram");

  const sidePanelActions = canAccessfastChannelsWrite
    ? [
        {
          label: t("calendarSidePanelDeleteActionLabel"),
          id: "calendarSidePanelDeleteActionLabel",
          isFullWidth: true,
          buttonVariant: BUTTON_VARIANTS.DELETE,
          onClick: (): void => setShowModal(true),
          isDisabled: isDeleteDisabled,
          tooltipOptions: isDeleteDisabled
            ? {
                id: "disabled-delete",
                content: tooltipContent,
              }
            : undefined,
        },
      ]
    : [];

  const confirmModalActions = [
    {
      label: t("cancel"),
      onClick: (): void => setShowModal(false),
      buttonVariant: BUTTON_VARIANTS.SECONDARY,
      isFullWidth: true,
    },
    {
      label: t("delete"),
      onClick: () =>
        deleteScheduleMutation.mutate({
          scheduleId: defaultSchedule.uuid,
        }),
      buttonVariant: BUTTON_VARIANTS.DELETE,
      isFullWidth: true,
    },
  ];

  return (
    <SidePanel
      actions={sidePanelActions}
      isOpen={isOpen}
      onClose={onClose}
      title={title}
    >
      <FormProvider
        {...methods} // eslint-disable-line react/jsx-props-no-spreading
      >
        <SidepanelBlock title={t("calendarScheduleStatistics")}>
          <div className="flex flex-col gap-[0.78125rem]">
            <h3 className="mt-[0.78125rem] font-semibold">{t("baseline")}</h3>
            <ObjectiveComponent
              borderColor={defaultSchedule?.color}
              completion={program.completion}
              current={program?.duration}
              objective={defaultSchedule?.daily_programs_objective}
              title={t("programs")}
            />
            <ObjectiveComponent
              borderColor={defaultSchedule?.color}
              completion={ads.completion}
              current={ads?.duration}
              objective={defaultSchedule?.daily_ads_objective}
              title={t("ads")}
            />
            <Input
              isCompact
              isReadOnly
              className="mb-[unset]"
              label={t("calendarSidePanelPlaylistDuration")}
              name="playlistDuration"
            />
            {variantList?.length !== 0 && (
              <React.Fragment>
                <h3 className="mt-[0.78125rem] font-semibold">
                  {t("variants")}
                </h3>
                <CalendarDaySidepanelVariantList
                  deleteScheduleMutation={deleteScheduleMutation.mutate}
                  isDeleteDisabled={isDeleteDisabled}
                  variantList={variantList}
                />
              </React.Fragment>
            )}
            {timeslotList?.length !== 0 && (
              <React.Fragment>
                <h3 className="mt-[0.78125rem] font-semibold">
                  {t("applied_timeslot")}
                </h3>
                <CalendarDaySidepanelTimeslotList
                  canAccessfastChannelsWrite={canAccessfastChannelsWrite}
                  timeslotList={timeslotList}
                />
              </React.Fragment>
            )}
          </div>
        </SidepanelBlock>
      </FormProvider>
      <ConfirmModal
        actions={confirmModalActions}
        iconPath={mdiBroom}
        isOpen={showModal}
        onClose={(): void => setShowModal(false)}
        size="sm"
        title={t("calendarSidePanelConfirmModalTitle")}
      >
        {t("calendarConfirmModalText")}
      </ConfirmModal>
    </SidePanel>
  );
}
